import React, { useState, useCallback, useMemo, memo } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Avatar,
   Button,
   Chip,
   Fade,
   Tooltip,
   alpha,
} from '@mui/material';
import {
   Link as LinkIcon,
   AccessTime as AccessTimeIcon,
   OpenInNew as OpenInNewIcon,
   TrendingUp as TrendingUpIcon,
   Analytics as AnalyticsIcon,
   Visibility as VisibilityIcon,
   QrCodeScanner as QrCodeScannerIcon,
   People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

// Interface for viewer info
export interface ViewerInfo {
   viewerId: string | null;
   viewerName: string;
   viewerEmail?: string | null;
   viewerPhotoURL?: string | null;
   viewerJobTitle?: string | null;
   source?: 'shareable_link' | 'qr_scan';
   viewedAt?: string;
   fingerprint?: string | null;
   isAnonymous?: boolean;
}

// Interface for the album link click data (for album owners)
export interface AlbumLinkClick {
   id: string; // Feed card ID
   albumId: string;
   albumTitle: string;
   albumCoverUrl?: string;
   albumPhotoCount: number;
   albumIsPublic: boolean;
   albumPhotos?: string[]; // Array of photo URLs for preview
   viewers?: ViewerInfo[]; // Array of viewers
   viewer?: ViewerInfo; // Single viewer (backwards compatibility / most recent)
   source: 'shareable_link' | 'qr_scan';
   totalLinkClicks: number;
   totalQRScans: number;
   percentageChange: number; // Percentage change in views
   clickedAt: Date | any;
   isFirstView: boolean; // Whether this is the first time this viewer saw the album
}

interface AlbumLinkClickCardProps {
   linkClick: AlbumLinkClick;
   onViewAlbum?: (albumId: string) => void;
}

// Helper function to convert Firebase timestamp to Date
const toDate = (timestamp: any): Date => {
   if (!timestamp) return new Date();
   if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
   }
   if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
   }
   if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      return new Date(timestamp._seconds * 1000);
   }
   if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
   }
   if (timestamp instanceof Date) {
      return timestamp;
   }
   return new Date(timestamp);
};

// Helper function to format relative time
const formatRelativeTime = (timestamp: any): string => {
   try {
      const date = toDate(timestamp);
      if (isNaN(date.getTime())) return 'Unknown';
      return formatDistanceToNow(date, { addSuffix: true });
   } catch {
      return 'Unknown';
   }
};

// Get source color config
const getSourceConfig = (source?: string) => {
   if (source === 'qr_scan') {
      return { bg: '#fef3c7', color: '#d97706', icon: <QrCodeScannerIcon sx={{ fontSize: 8, color: '#fff' }} /> };
   }
   return { bg: '#fce7f3', color: '#EC4899', icon: <LinkIcon sx={{ fontSize: 8, color: '#fff' }} /> };
};

// Extended viewer info with view count
interface DisplayViewer extends ViewerInfo {
   viewCount?: number;
   anonymousCount?: number;
   linkViewCount?: number;
   qrViewCount?: number;
}

const AlbumLinkClickCard: React.FC<AlbumLinkClickCardProps> = memo(({
   linkClick,
   onViewAlbum,
}) => {
   const navigate = useNavigate();
   const [isVisible] = useState(true);

   // Memoize all viewers processing to avoid recalculation on every render
   const {
      displayViewers,
      totalViewsCount,
      uniqueViewersCount,
      linkViewersCount,
      qrViewersCount,
   } = useMemo(() => {
      // Get all viewers array - fallback to single viewer if viewers array doesn't exist
      const allViewers = linkClick.viewers && linkClick.viewers.length > 0 
         ? linkClick.viewers 
         : linkClick.viewer 
            ? [linkClick.viewer] 
            : [];

      // Group viewers by unique user (by viewerId or viewerEmail for logged-in, fingerprint for anonymous)
      const groupedViewersMap = new Map<string, DisplayViewer>();
      const anonymousViews: ViewerInfo[] = [];

      allViewers.forEach((viewer) => {
         const isAnonymous = !viewer.viewerId && (!viewer.viewerName || viewer.viewerName === 'Anonymous' || viewer.viewerName === 'Unknown User');
         
         if (isAnonymous) {
            anonymousViews.push(viewer);
         } else {
            const key = viewer.viewerId || viewer.viewerEmail || viewer.viewerName || 'unknown';
            
            if (groupedViewersMap.has(key)) {
               const existing = groupedViewersMap.get(key)!;
               existing.viewCount = (existing.viewCount || 1) + 1;
               if (viewer.viewedAt && (!existing.viewedAt || viewer.viewedAt > existing.viewedAt)) {
                  existing.viewedAt = viewer.viewedAt;
                  existing.source = viewer.source;
               }
               if (viewer.source === 'shareable_link') {
                  existing.linkViewCount = (existing.linkViewCount || 0) + 1;
               } else if (viewer.source === 'qr_scan') {
                  existing.qrViewCount = (existing.qrViewCount || 0) + 1;
               }
            } else {
               groupedViewersMap.set(key, {
                  ...viewer,
                  viewCount: 1,
                  linkViewCount: viewer.source === 'shareable_link' ? 1 : 0,
                  qrViewCount: viewer.source === 'qr_scan' ? 1 : 0,
               });
            }
         }
      });

      // Convert map to array and sort by most recent
      const uniqueNamedViewers = Array.from(groupedViewersMap.values()).sort((a, b) => {
         const aTime = a.viewedAt ? new Date(a.viewedAt).getTime() : 0;
         const bTime = b.viewedAt ? new Date(b.viewedAt).getTime() : 0;
         return bTime - aTime;
      });

      // Group anonymous viewers stats
      const anonymousLinkCount = anonymousViews.filter(v => v.source === 'shareable_link').length;
      const anonymousQRCount = anonymousViews.filter(v => v.source === 'qr_scan').length;
      const totalAnonymousCount = anonymousViews.length;

      // Create display viewers array
      const displayViewers: DisplayViewer[] = [...uniqueNamedViewers];
      
      if (totalAnonymousCount > 0) {
         const mostRecentAnonymous = anonymousViews[0];
         displayViewers.push({
            viewerId: null,
            viewerName: 'Anonymous',
            viewerEmail: null,
            viewerPhotoURL: null,
            viewerJobTitle: null,
            source: mostRecentAnonymous?.source || 'shareable_link',
            viewedAt: mostRecentAnonymous?.viewedAt,
            isAnonymous: true,
            anonymousCount: totalAnonymousCount,
            linkViewCount: anonymousLinkCount,
            qrViewCount: anonymousQRCount,
         });
      }

      return {
         displayViewers,
         totalViewsCount: allViewers.length,
         uniqueViewersCount: uniqueNamedViewers.length + (totalAnonymousCount > 0 ? 1 : 0),
         linkViewersCount: allViewers.filter(v => v.source === 'shareable_link').length,
         qrViewersCount: allViewers.filter(v => v.source === 'qr_scan').length,
      };
   }, [linkClick.viewers, linkClick.viewer]);

   // Memoize other computed values
   const isQRScan = linkClick.source === 'qr_scan';
   const sourceLabel = isQRScan ? 'QR Scan' : 'Link';

   const percentageChangeText = useMemo(() => {
      if (linkClick.percentageChange > 0) return `+${linkClick.percentageChange.toFixed(0)}%`;
      if (linkClick.percentageChange < 0) return `${linkClick.percentageChange.toFixed(0)}%`;
      return '0%';
   }, [linkClick.percentageChange]);
   
   const isPositiveChange = linkClick.percentageChange > 0;

   // Handle view album
   const handleViewAlbum = useCallback(() => {
      if (onViewAlbum) {
         onViewAlbum(linkClick.albumId);
      } else {
         navigate(`/albums/${linkClick.albumId}`);
      }
   }, [linkClick.albumId, navigate, onViewAlbum]);

   return (
      <Fade in={isVisible} timeout={300}>
         <Card
            sx={{
               borderRadius: 2,
               boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
               border: '1px solid #fce7f3',
               borderLeft: '3px solid #00C4A7',
               overflow: 'visible',
               background: 'linear-gradient(135deg, #fdf2f8 0%, #ffffff 50%, #fce7f3 100%)',
               transition: 'all 0.2s ease-in-out',
               '&:hover': {
                  boxShadow: '0 4px 16px rgba(236, 72, 153, 0.15)',
                  transform: 'translateY(-1px)',
               },
            }}
         >
            <CardContent sx={{ p: 2.5 }}>
               {/* Header with Badge */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'flex-start',
                     mb: 2,
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                     <AnalyticsIcon sx={{ color: '#EC4899', fontSize: 20 }} />
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {linkClick.albumTitle}
                     </Typography>
                     <Chip
                        label="Analytics"
                        size="small"
                        sx={{
                           height: 20,
                           fontSize: '0.7rem',
                           fontWeight: 600,
                           backgroundColor: '#EC4899',
                           color: '#ffffff',
                        }}
                     />
                  </Box>

                  <Chip
                     icon={isQRScan ? <QrCodeScannerIcon sx={{ fontSize: 14 }} /> : <LinkIcon sx={{ fontSize: 14 }} />}
                     label={sourceLabel}
                     size="small"
                     sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: '#EC4899',
                        color: '#ffffff',
                        '& .MuiChip-icon': {
                           color: '#ffffff',
                        },
                     }}
                  />
               </Box>

               {/* Viewers Section */}
               <Box
                  sx={{
                     p: 1.5,
                     mb: 2,
                     backgroundColor: '#ffffff',
                     borderRadius: 1.5,
                     border: '1px solid #fce7f3',
                     overflow: 'visible',
                  }}
               >
                  {/* Viewers Header with Stats */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 18, color: '#EC4899' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                           {uniqueViewersCount} Viewer{uniqueViewersCount !== 1 ? 's' : ''}
                           <Typography component="span" variant="caption" sx={{ color: '#6b7280', ml: 0.5 }}>
                              ({totalViewsCount} total view{totalViewsCount !== 1 ? 's' : ''})
                           </Typography>
                        </Typography>
                     </Box>
                  </Box>

                  {/* Viewer Stats Chips */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                     {linkViewersCount > 0 && (
                        <Chip
                           icon={<LinkIcon sx={{ fontSize: 14 }} />}
                           label={`${linkViewersCount} via Link`}
                           size="small"
                           sx={{
                              height: 24,
                              backgroundColor: '#fce7f3',
                              color: '#EC4899',
                              fontWeight: 500,
                           }}
                        />
                     )}
                     {qrViewersCount > 0 && (
                        <Chip
                           icon={<QrCodeScannerIcon sx={{ fontSize: 14 }} />}
                           label={`${qrViewersCount} via QR`}
                           size="small"
                           sx={{
                              height: 24,
                              backgroundColor: '#fef3c7',
                              color: '#d97706',
                              fontWeight: 500,
                           }}
                        />
                     )}
                  </Box>

                  {/* Horizontal Scrollable Viewers List */}
                  <Box
                     sx={{
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                           height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                           backgroundColor: '#fdf2f8',
                           borderRadius: 3,
                        },
                        '&::-webkit-scrollbar-thumb': {
                           backgroundColor: '#f9a8d4',
                           borderRadius: 3,
                           '&:hover': {
                              backgroundColor: '#EC4899',
                           },
                        },
                     }}
                  >
                     <Box 
                        sx={{ 
                           display: 'inline-flex',
                           flexDirection: 'row',
                           flexWrap: 'nowrap',
                           gap: 1.5, 
                           minWidth: 'min-content',
                           width: 'max-content',
                        }}
                     >
                        {displayViewers.map((viewer, index) => {
                           const sourceConfig = getSourceConfig(viewer.source);
                           const isAnonymousCard = viewer.isAnonymous && viewer.anonymousCount;
                           const anonymousCount = viewer.anonymousCount || 0;
                           const viewCount = viewer.viewCount || 1;
                           const hasMultipleViews = viewCount > 1 || anonymousCount > 1;
                           
                           return (
                              <Box
                                 key={index}
                                 sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: 110,
                                    minWidth: 110,
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: isAnonymousCard 
                                       ? alpha('#6b7280', 0.05)
                                       : alpha(sourceConfig.color, 0.05),
                                    border: `1px solid ${isAnonymousCard 
                                       ? alpha('#6b7280', 0.2)
                                       : alpha(sourceConfig.color, 0.2)}`,
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                       backgroundColor: isAnonymousCard 
                                          ? alpha('#6b7280', 0.1)
                                          : alpha(sourceConfig.color, 0.1),
                                       transform: 'translateY(-2px)',
                                       boxShadow: `0 4px 12px ${isAnonymousCard 
                                          ? alpha('#6b7280', 0.15)
                                          : alpha(sourceConfig.color, 0.15)}`,
                                    },
                                 }}
                              >
                                 {/* View count badge for users who viewed multiple times */}
                                 {hasMultipleViews && (
                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          top: -8,
                                          right: -8,
                                          minWidth: 24,
                                          height: 24,
                                          borderRadius: '12px',
                                          backgroundColor: isAnonymousCard ? '#6b7280' : sourceConfig.color,
                                          color: '#fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          border: '2px solid #fff',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                          px: 0.5,
                                       }}
                                    >
                                       {isAnonymousCard ? anonymousCount : viewCount}
                                    </Box>
                                 )}

                                 {/* Avatar with source indicator */}
                                 <Box sx={{ position: 'relative', mb: 1 }}>
                                    <Avatar
                                       src={viewer.viewerPhotoURL || undefined}
                                       sx={{
                                          width: 44,
                                          height: 44,
                                          fontSize: '1rem',
                                          fontWeight: 600,
                                          border: `3px solid ${isAnonymousCard ? '#6b7280' : sourceConfig.color}`,
                                          backgroundColor: isAnonymousCard 
                                             ? alpha('#6b7280', 0.2)
                                             : alpha(sourceConfig.color, 0.2),
                                          color: isAnonymousCard ? '#6b7280' : sourceConfig.color,
                                       }}
                                    >
                                       {viewer.viewerName?.[0]?.toUpperCase() || 'A'}
                                    </Avatar>
                                    {/* Source indicator dot */}
                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          right: 0,
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          backgroundColor: isAnonymousCard ? '#6b7280' : sourceConfig.color,
                                          border: '2px solid #fff',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                       }}
                                    >
                                       {sourceConfig.icon}
                                    </Box>
                                 </Box>

                                 {/* Name */}
                                 <Typography
                                    variant="body2"
                                    sx={{
                                       fontWeight: 600,
                                       color: '#374151',
                                       textAlign: 'center',
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                       whiteSpace: 'nowrap',
                                       width: '100%',
                                       lineHeight: 1.2,
                                       fontSize: '0.8rem',
                                    }}
                                 >
                                    {isAnonymousCard ? 'Anonymous' : (viewer.viewerName || 'Unknown')}
                                 </Typography>

                                 {/* View count subtitle or Job Title/Email */}
                                 <Tooltip title={viewer.viewerEmail || viewer.viewerJobTitle || ''} arrow>
                                    <Typography
                                       variant="caption"
                                       sx={{
                                          color: '#6b7280',
                                          textAlign: 'center',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          width: '100%',
                                          fontSize: '0.65rem',
                                          lineHeight: 1.3,
                                       }}
                                    >
                                       {isAnonymousCard 
                                          ? `${anonymousCount} view${anonymousCount !== 1 ? 's' : ''}`
                                          : viewCount > 1 
                                             ? `${viewCount} views`
                                             : (viewer.viewerJobTitle || viewer.viewerEmail || 'Viewer')}
                                    </Typography>
                                 </Tooltip>

                                 {/* Source Chips - show breakdown for multiple views */}
                                 {isAnonymousCard ? (
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
                                       {(viewer.linkViewCount || 0) > 0 && (
                                          <Chip
                                             label={`${viewer.linkViewCount} Link`}
                                             size="small"
                                             sx={{
                                                height: 16,
                                                fontSize: '0.55rem',
                                                fontWeight: 600,
                                                backgroundColor: '#fce7f3',
                                                color: '#EC4899',
                                                '& .MuiChip-label': { px: 0.5 },
                                             }}
                                          />
                                       )}
                                       {(viewer.qrViewCount || 0) > 0 && (
                                          <Chip
                                             label={`${viewer.qrViewCount} QR`}
                                             size="small"
                                             sx={{
                                                height: 16,
                                                fontSize: '0.55rem',
                                                fontWeight: 600,
                                                backgroundColor: '#fef3c7',
                                                color: '#d97706',
                                                '& .MuiChip-label': { px: 0.5 },
                                             }}
                                          />
                                       )}
                                    </Box>
                                 ) : viewCount > 1 && ((viewer.linkViewCount || 0) > 0 || (viewer.qrViewCount || 0) > 0) ? (
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
                                       {(viewer.linkViewCount || 0) > 0 && (
                                          <Chip
                                             label={`${viewer.linkViewCount} Link`}
                                             size="small"
                                             sx={{
                                                height: 16,
                                                fontSize: '0.55rem',
                                                fontWeight: 600,
                                                backgroundColor: '#fce7f3',
                                                color: '#EC4899',
                                                '& .MuiChip-label': { px: 0.5 },
                                             }}
                                          />
                                       )}
                                       {(viewer.qrViewCount || 0) > 0 && (
                                          <Chip
                                             label={`${viewer.qrViewCount} QR`}
                                             size="small"
                                             sx={{
                                                height: 16,
                                                fontSize: '0.55rem',
                                                fontWeight: 600,
                                                backgroundColor: '#fef3c7',
                                                color: '#d97706',
                                                '& .MuiChip-label': { px: 0.5 },
                                             }}
                                          />
                                       )}
                                    </Box>
                                 ) : (
                                    <Chip
                                       label={viewer.source === 'qr_scan' ? 'QR Scan' : 'Link'}
                                       size="small"
                                       sx={{
                                          mt: 0.75,
                                          height: 18,
                                          fontSize: '0.6rem',
                                          fontWeight: 600,
                                          backgroundColor: sourceConfig.bg,
                                          color: sourceConfig.color,
                                          '& .MuiChip-label': {
                                             px: 0.75,
                                          },
                                       }}
                                    />
                                 )}

                                 {/* View Time */}
                                 {viewer.viewedAt && (
                                    <Typography
                                       variant="caption"
                                       sx={{
                                          color: '#9ca3af',
                                          fontSize: '0.55rem',
                                          mt: 0.5,
                                          textAlign: 'center',
                                       }}
                                    >
                                       {(isAnonymousCard || viewCount > 1) ? 'Latest: ' : ''}{formatRelativeTime(viewer.viewedAt)}
                                    </Typography>
                                 )}
                              </Box>
                           );
                        })}
                     </Box>
                  </Box>
               </Box>

               {/* Stats Section */}
               <Box
                  sx={{
                     display: 'flex',
                     gap: 1.5,
                     mb: 2,
                     p: 1.5,
                     backgroundColor: '#fdf2f8',
                     borderRadius: 1.5,
                     border: '1px solid #fce7f3',
                  }}
               >
                  {/* Total Views */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <VisibilityIcon sx={{ fontSize: 16, color: '#EC4899' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#EC4899', lineHeight: 1 }}>
                           {linkClick.totalLinkClicks + linkClick.totalQRScans}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                        Total Views
                     </Typography>
                  </Box>

                  {/* Percentage Change */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TrendingUpIcon 
                           sx={{ 
                              fontSize: 16, 
                              color: isPositiveChange ? '#10b981' : linkClick.percentageChange < 0 ? '#ef4444' : '#6b7280',
                           }} 
                        />
                        <Typography 
                           variant="h6" 
                           sx={{ 
                              fontWeight: 700, 
                              color: isPositiveChange ? '#10b981' : linkClick.percentageChange < 0 ? '#ef4444' : '#6b7280',
                              lineHeight: 1,
                           }}
                        >
                           {percentageChangeText}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                        Change
                     </Typography>
                  </Box>

                  {/* Link Clicks */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <LinkIcon sx={{ fontSize: 16, color: '#EC4899' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', lineHeight: 1 }}>
                           {linkClick.totalLinkClicks}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                        Link Clicks
                     </Typography>
                  </Box>

                  {/* QR Scans */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <QrCodeScannerIcon sx={{ fontSize: 16, color: '#d97706' }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#374151', lineHeight: 1 }}>
                           {linkClick.totalQRScans}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                        QR Scans
                     </Typography>
                  </Box>
               </Box>

               {/* Timestamp */}
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <AccessTimeIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                     Last viewed {formatRelativeTime(linkClick.clickedAt)}
                  </Typography>
                  {linkClick.isFirstView && (
                     <Chip
                        label="First View"
                        size="small"
                        sx={{
                           height: 18,
                           fontSize: '0.6rem',
                           fontWeight: 600,
                           backgroundColor: '#fef3c7',
                           color: '#d97706',
                           ml: 1,
                        }}
                     />
                  )}
               </Box>

               {/* Action Button */}
               <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  onClick={handleViewAlbum}
                  sx={{
                     backgroundColor: '#EC4899',
                     color: '#ffffff',
                     fontWeight: 600,
                     textTransform: 'none',
                     borderRadius: 1.5,
                     py: 1,
                     '&:hover': { backgroundColor: '#db2777' },
                  }}
               >
                  View Album
               </Button>
            </CardContent>
         </Card>
      </Fade>
   );
});

// Display name for debugging
AlbumLinkClickCard.displayName = 'AlbumLinkClickCard';

export default AlbumLinkClickCard;
